import { useEffect, useState } from "react";
import {
  Card,
  notification,
  Table,
  Tooltip,
  Tag,
  Button,
  Row,
  Col,
  Space,
} from "antd";
import {
  getNotesApprenants,
  getUploadPvSession,
} from "../../../../api/geoprod";
import { useLoader } from "../../../../context/LoaderContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProfileOutlined } from "@ant-design/icons";
import CustomUpload from "../../../../utils/CustomUpload";
import classes from "../../../Etablissements/Etablissements.module.css";
import { BookOpen } from "lucide-react";

const ListeNotesApprenants = () => {
  const { setIsLoading } = useLoader();
  const [apprenants, setApprenants] = useState([]);
  const [columns, setColumns] = useState([]);
  const params = useLocation();
  const id_affaire = params?.state?.id_affaire;
  const periode = params?.state?.periode
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await getUploadPvSession(id_affaire);
        setDocuments(response?.pvs);

        const { list: rawData } = await getNotesApprenants( id_affaire, { "periode": periode });
        
        const apprenantsData = {};
        const modulesStructure = {};

        Object.entries(rawData).forEach(([annee, anneeData]) => {
          if (annee === "validation_note_final" || annee === "meuilleur_note") return;

          modulesStructure[annee] = {};

          Object.entries(anneeData).forEach(([semestre, semestreData]) => {
            if (semestre === "valide_note") return;

            modulesStructure[annee][semestre] = {};

            if (semestreData.examen?.length > 0) {
              semestreData.examen.forEach(note => {
                if (!note.id_apprenant) return;

                const apprenantId = note.id_apprenant;
                const moduleKey = note.key_module;

                if (!apprenantsData[apprenantId]) {
                  apprenantsData[apprenantId] = {
                    key: apprenantId,
                    fullName: `${note.surname || ''} ${note.name || ''}`.trim(),
                    notes: {},
                    decisions: {}
                  };
                }

                const noteKey = `${annee}-${semestre}-examen-${moduleKey}`;
                apprenantsData[apprenantId].notes[noteKey] = {
                  note: note.note,
                  reussi: note.reussi
                };

                if (!modulesStructure[annee][semestre][moduleKey]) {
                  modulesStructure[annee][semestre][moduleKey] = {
                    name: note.name_module,
                    seuil: note.seuil,
                    unite_seuil: note.unite_seuil,
                    hasExamen: true,
                    hasRattrapage: false
                  };
                }
              });
            }

            if (semestreData.rattrapage?.length > 0) {
              semestreData.rattrapage.forEach(note => {
                if (!note.id_apprenant) return;

                const apprenantId = note.id_apprenant;
                const moduleKey = note.key_module;

                if (!apprenantsData[apprenantId]) {
                  apprenantsData[apprenantId] = {
                    key: apprenantId,
                    fullName: `${note.surname || ''} ${note.name || ''}`.trim(),
                    notes: {},
                    decisions: {}
                  };
                }

                const noteKey = `${annee}-${semestre}-rattrapage-${moduleKey}`;
                apprenantsData[apprenantId].notes[noteKey] = {
                  note: note.note,
                  reussi: note.reussi
                };

                if (!modulesStructure[annee][semestre][moduleKey]) {
                  modulesStructure[annee][semestre][moduleKey] = {
                    name: note.name_module,
                    seuil: note.seuil,
                    unite_seuil: note.unite_seuil,
                    hasExamen: false,
                    hasRattrapage: true
                  };
                } else {
                  modulesStructure[annee][semestre][moduleKey].hasRattrapage = true;
                }
              });
            }

            if (semestreData.decisions?.length > 0) {
              semestreData.decisions.forEach(decision => {
                if (!decision.id_apprenant) return;

                const apprenantId = decision.id_apprenant;
                
                if (!apprenantsData[apprenantId]) {
                  apprenantsData[apprenantId] = {
                    key: apprenantId,
                    fullName: "", 
                    notes: {},
                    decisions: {}
                  };
                }

                apprenantsData[apprenantId].decisions[semestre] = {
                  decision: decision.directeur_decision,
                  commentaire: decision.directeur_commentaire
                };
              });
            }
          });
        });

        const tableColumns = [
          {
            title: "Apprenant",
            dataIndex: "fullName",
            key: "fullName",
            fixed: "left",
            width: 180,
            render: (text) => text || "-",
          }
        ];

        Object.entries(modulesStructure).forEach(([annee, semestres]) => {
          const anneeColumns = [];

          Object.entries(semestres).forEach(([semestre, modules]) => {
            const semestreColumns = [];
            let hasDecisionInSemestre = false;

            Object.values(apprenantsData).forEach(apprenant => {
              if (apprenant.decisions[semestre]?.decision !== undefined || 
                  apprenant.decisions[semestre]?.commentaire) {
                hasDecisionInSemestre = true;
              }
            });

            Object.entries(modules).forEach(([moduleKey, module]) => {
              const moduleColumns = [];

              if (module.hasExamen) {
                moduleColumns.push({
                  title: "Examen",
                  key: `${annee}-${semestre}-examen-${moduleKey}`,
                  width: 100,
                  render: (_, record) => {
                    const note = record.notes[`${annee}-${semestre}-examen-${moduleKey}`];
                    return note?.note !== undefined ? note.note : "-";
                  }
                });
              }

              if (module.hasRattrapage) {
                moduleColumns.push({
                  title: "Rattrapage",
                  key: `${annee}-${semestre}-rattrapage-${moduleKey}`,
                  width: 100,
                  render: (_, record) => {
                    const note = record.notes[`${annee}-${semestre}-rattrapage-${moduleKey}`];
                    return note?.note !== undefined ? note.note : "-";
                  }
                });
              }

              moduleColumns.push({
                title: "Résultat",
                key: `${annee}-${semestre}-resultat-${moduleKey}`,
                width: 100,
                render: (_, record) => {
                  const examen = record.notes[`${annee}-${semestre}-examen-${moduleKey}`];
                  const rattrapage = record.notes[`${annee}-${semestre}-rattrapage-${moduleKey}`];

                  if (rattrapage) {
                    return rattrapage.reussi === 1 ? (
                      <Tag color="green">Validé</Tag>
                    ) : (
                      <Tag color="red">Non validé</Tag>
                    );
                  }

                  if (examen) {
                    return examen.reussi === 1 ? (
                      <Tag color="green">Validé</Tag>
                    ) : (
                      <Tag color="red">Non validé</Tag>
                    );
                  }

                  return "-";
                }
              });

              semestreColumns.push({
                title: (
                  <Tooltip title={module.name}>
                    {`${moduleKey} (seuil: ${module.seuil || ''} ${module.unite_seuil || ''})`}
                  </Tooltip>
                ),
                children: moduleColumns
              });
            });

            if (hasDecisionInSemestre) {
              semestreColumns.push({
                title: "Décision",
                key: `${annee}-${semestre}-decision`,
                width: 120,
                render: (_, record) => {
                  const decision = record.decisions[semestre]?.decision;
                  return decision === 1 ? (
                    <Tag color="green">Accepté</Tag>
                  ) : decision === 0 ? (
                    <Tag color="red">Refusé</Tag>
                  ) : <Tag color="red">Pas encore</Tag>;
                }
              });

              semestreColumns.push({
                title: "Commentaire",
                key: `${annee}-${semestre}-commentaire`,
                width: 150,
                render: (_, record) => record.decisions[semestre]?.commentaire || "-"
              });
            }

            anneeColumns.push({
              title: semestre,
              children: semestreColumns
            });
          });

          tableColumns.push({
            title: annee,
            children: anneeColumns
          });
        });

        const dataSource = Object.values(apprenantsData);

        setColumns(tableColumns);
        setApprenants(dataSource);
      } catch (error) {
        console.error("Erreur:", error);
        notification.error({
          message: t("Erreur"),
          description: error?.data?.message || t("Erreur lors du chargement"),
          duration: 5,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div>
      <Space className={classes.rowButtonCreate}>
        <Button
          className={classes.buttonCreate}
          onClick={() => {
            navigate("/sessionFormationHomologuee/getAllExams", {
              state: { id_affaire }
            });
          }}
        >
          <BookOpen strokeWidth={1.25} />
          {t("Liste des examens et des rattrapages")}
        </Button>
      </Space>
      
      <Card title={"Liste des PV de la session"} style={{ marginBottom: '24px' }}>
        {Object.entries(documents)?.map(([annee, semestres]) => (
          <div key={annee} style={{ marginBottom: '16px', paddingInline: "20px" }}>
            <div>
              <ProfileOutlined style={{ marginRight: '8px', color: '#3f51b5' }} />
              <span style={{ fontWeight: 600, color: '#3f51b5' }}>{annee}</span>
            </div>
            
            <Row gutter={16}>
              {Object.entries(semestres)?.map(([semestre, fichiers]) => {
                const dernierDoc = fichiers?.[fichiers.length - 1]?.url;
                return (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} key={semestre}>
                    <div style={{ display: 'flex', alignItems: 'center', paddingBlock: "15px", paddingInline: "30px" }}>
                      <span style={{ fontWeight: 500, marginRight: '12px', color: '#595959' }}>
                        {semestre} :
                      </span>
                      <div>
                        <CustomUpload
                          field={{
                            buttonName: "Ajouter PV",
                            name: `pv_session_${annee}_${semestre}`,
                          }}
                          multiple={false}
                          accept=".png,.jpg,.jpeg,.pdf"
                          customRequest={null}
                          id={semestre}
                          setSelectedDiplomaFiles={null}
                          type="diplome"
                          value={dernierDoc}
                          showIcon={false}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        ))}
      </Card>
      
      <Card title="Liste des notes">
        <Table
          size="small"
          columns={columns}
          dataSource={apprenants}
          className="compact-table"
          bordered
          scroll={{ x: "max-content" }}
        />
      </Card>
    </div>
  );
};

export default ListeNotesApprenants;